/* One motion owner for the mobile-layout document. Geometry is supplied by
 * Cards; this controller knows nothing about Store, About, headers or colors.
 * Card gestures cross one boundary; products and the footer use native wheel
 * scrolling. Ownership switches only at a boundary, never between two tweens.
 * Decorative reveal animations do not participate in gesture state. */
(function () {
  'use strict';
  var artworkCache = new Map();
  function maskSource(element) {
    var mask = getComputedStyle(element).maskImage || getComputedStyle(element).webkitMaskImage;
    var match = mask && mask.match(/^url\(["']?(.*?)["']?\)$/);
    return match && match[1];
  }
  // Illustrator exports can contain megabytes of path coordinates. Cache the
  // browser's decoded silhouette at display resolution instead of repeatedly
  // repainting those paths while a card scrolls and the symbol rotates. This
  // changes neither the selected source file nor its editor-managed color.
  window.MwyPrepareCardMasks = function (container) {
    if (!container) return;
    var symbols = Array.from(container.querySelectorAll('.mwy-about-card__symbol'));
    var pending = [];
    function preview(element) {
      element.setAttribute('data-mwy-mask-ready', 'true');
      if (container.querySelector('[data-mwy-mask-preview]')) return;
      element.setAttribute('data-mwy-mask-preview', 'true');
      var headline = container.querySelectorAll('.mwy-about-card__headline')[symbols.indexOf(element)];
      if (headline) headline.setAttribute('data-mwy-mask-preview', 'true');
    }
    symbols.forEach(function (element) {
      if (element.dataset.mwyMaskLoading) return;
      element.dataset.mwyMaskLoading = 'true';
      var selected = element.querySelector('img');
      if (selected) {
        pending.push(new Promise(function(resolve) {
          function loaded() { if(selected.naturalWidth) preview(element); resolve(); }
          if(selected.complete) loaded();
          else { selected.addEventListener('load',loaded,{once:true}); selected.addEventListener('error',resolve,{once:true}); }
        }));
        return;
      }
      var source = maskSource(element);
      if (!source || !/\.svg(?:\?|$)/i.test(source)) { preview(element); return; }
      var pixels = Math.ceil(Math.max(180, element.clientWidth) * Math.max(2, Math.min(3, devicePixelRatio || 1)));
      var key = source + ':' + pixels;
      if (!artworkCache.has(key)) artworkCache.set(key, new Promise(function (resolve) {
        var image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = function () {
          // Decoding and caching is startup work, never a scroll-frame task.
          var render = function () {
            try {
              var canvas = document.createElement('canvas');
              canvas.width = canvas.height = pixels;
              var ratio = Math.min(pixels / image.naturalWidth, pixels / image.naturalHeight);
              var width = image.naturalWidth * ratio, height = image.naturalHeight * ratio;
              canvas.getContext('2d').drawImage(image, (pixels - width) / 2, (pixels - height) / 2, width, height);
              resolve(canvas.toDataURL('image/png'));
            } catch (error) { resolve(null); } // Original SVG remains the fallback.
          };
          if ('requestIdleCallback' in window) requestIdleCallback(render, { timeout: 1500 });
          else setTimeout(render, 0);
        };
        image.onerror = function () { resolve(null); };
        image.src = source;
      }));
      pending.push(artworkCache.get(key).then(function (image) {
        if (image && element.isConnected && maskSource(element) === source) {
          element.style.setProperty('--mwy-symbol-url', 'url("' + image + '")');
          preview(element);
        }
      }));
    });
    if (pending.length) Promise.all(pending).then(function() {
      var ready = symbols.find(function(element) { return element.hasAttribute('data-mwy-mask-ready'); });
      if (!ready) return;
      symbols.forEach(function(element) {
        if (element.hasAttribute('data-mwy-mask-ready')) return;
        // A failed artwork request must never produce an empty animation slot.
        var selected = element.querySelector('img');
        if (selected) selected.remove();
        element.style.setProperty('--mwy-symbol-url', getComputedStyle(ready).getPropertyValue('--mwy-symbol-url'));
      });
      container.setAttribute('data-mwy-masks-ready','true');
    });
  };
  window.MwyCardScroll = function (options) {
    var abort = new AbortController();
    var frame = 0, idle = 0, gesture = null, touch = null;
    var target = window.scrollY, lastWheel = 0, tween = null;
    var wheelMagnitude = 0, wheelDirection = 0, wheelPeak = 0, wheelTail = false;
    var reduced = matchMedia('(prefers-reduced-motion: reduce)');
    var root = document.scrollingElement;
    var originalSnap = root.style.getPropertyValue('scroll-snap-type');
    var originalBehavior = root.style.getPropertyValue('scroll-behavior');
    var originalSnapPriority = root.style.getPropertyPriority('scroll-snap-type');
    var originalBehaviorPriority = root.style.getPropertyPriority('scroll-behavior');
    root.style.setProperty('scroll-snap-type', 'none', 'important');
    root.style.setProperty('scroll-behavior', 'auto', 'important');

    function clamp(value, min, max) { return Math.max(min, Math.min(max, value)); }
    function points() { return options.points(); }
    function limit() { return Math.max(0, root.scrollHeight - root.clientHeight); }
    function write(y) {
      window.scrollTo({ top: y, behavior: 'instant' });
      // Standalone card pages can synchronize their counter-transform with
      // this exact scroll write instead of correcting it one frame later.
      if (options.onWrite) options.onWrite();
    }
    function stop() {
      cancelAnimationFrame(frame); clearTimeout(idle);
      frame = idle = 0; tween = null;
    }
    function nativeScroll() {
      return options.nativeScroll && options.nativeScroll(window.scrollY);
    }
    function excluded(event) {
      if (!event.target.closest || event.target.closest('.mwy-nav-reveal, .drawer, [role="dialog"], .dropdown-menu, input, textarea, select, [contenteditable="true"], [data-native-scroll]')) return true;
      if (!options.content.contains(event.target) && !event.target.closest('#HeaderWrapper, .announcement')) return true;
      // Carousels and independently scrollable widgets retain their controls.
      for (var el = event.target; el && el !== options.content && el !== document.body && el !== root; el = el.parentElement) {
        var css = getComputedStyle(el);
        if (/(auto|scroll)/.test(css.overflowY) && el.scrollHeight > el.clientHeight + 2) return true;
      }
      return false;
    }
    function begin(reference) {
      stop();
      var anchors = points(), y = typeof reference === 'number' ? reference : window.scrollY;
      var index = 0;
      for (var i = 0; i < anchors.length; i++) if (anchors[i] <= y + 2) index = i;
      var nearest = anchors.reduce(function (best, value, n) {
        return Math.abs(value - y) < Math.abs(anchors[best] - y) ? n : best;
      }, 0);
      var interior = index === anchors.length - 1 && y > anchors[index] + 2;
      var between = !interior && Math.abs(anchors[nearest] - y) > 2;
      var floor = index;
      if (!interior) index = nearest;
      gesture = {
        anchors: anchors, index: index, start: y, travel: 0, direction: 0,
        lower: between ? anchors[floor] : interior ? anchors[index] : anchors[Math.max(0, index - 1)],
        upper: between ? anchors[floor + 1] : index === anchors.length - 1 ? limit() : anchors[index + 1],
        interior: interior, between: between, velocity: 0
      };
      target = window.scrollY;
    }
    function destination() {
      if (!gesture) return target;
      var g = gesture, last = g.anchors.length - 1;
      if (g.interior) return clamp(target, g.lower, g.upper);
      if (g.index === last && target >= g.anchors[last] && g.travel >= 0) return target;
      // Travel is measured from the start of this gesture, not from an
      // accumulating history of earlier wheel packets or another controller.
      var intention = Math.abs(g.travel) >= 18 || Math.abs(g.velocity) > .25;
      if (g.between && intention) return g.travel > 0 ? g.upper : g.lower;
      var index = intention ? g.index + Math.sign(g.travel || g.velocity) : g.index;
      return g.anchors[clamp(index, 0, last)];
    }
    function animate(to, done) {
      cancelAnimationFrame(frame);
      var destinationIndex = options.dynamicPoints ? points().findIndex(function (point) { return Math.abs(point - to) < 2; }) : -1;
      var from = window.scrollY;
      var start = performance.now();
      var duration = reduced.matches ? 0 : clamp(260 + Math.abs(to - from) * .28, 260, 560);
      tween = { to: to };
      function tick(now) {
        if (destinationIndex >= 0) {
          to = points()[destinationIndex];
          tween.to = to;
        }
        var progress = duration ? clamp((now - start) / duration, 0, 1) : 1;
        // Modest starting velocity, zero ending velocity. A cubic ease-out
        // alone starts a full-card settle at its maximum speed and feels like
        // a kick after the wheel/finger has slowed down.
        var ease = 2.6 * progress * progress - 1.8 * progress * progress * progress + .2 * progress;
        write(from + (to - from) * ease);
        if (progress < 1) frame = requestAnimationFrame(tick);
        else { frame = 0; tween = null; if (done) done(); }
      }
      frame = requestAnimationFrame(tick);
    }
    function follow() {
      if (frame) return;
      var previous = performance.now();
      function tick(now) {
        var delta = target - window.scrollY;
        // A frame's timestamp can precede the wheel callback that scheduled
        // it. Never let a negative first delta move opposite to the gesture.
        var dt = Math.max(0, now - previous);
        var response = 75;
        var fraction = reduced.matches ? 1 : 1 - Math.exp(-dt / response);
        previous = now;
        if (Math.abs(delta) < .6) { write(target); frame = 0; return; }
        write(window.scrollY + delta * fraction);
        frame = requestAnimationFrame(tick);
      }
      frame = requestAnimationFrame(tick);
    }
    function finishWheel() {
      idle = 0;
      if (!gesture) return;
      if (gesture.native) return;
      var to = destination();
      target = to;
      animate(to);
    }
    function wheel(event) {
      if (nativeScroll()) { stop(); gesture = null; return; }
      // Trackpads rarely travel on a mathematically perfect vertical axis. A
      // strong vertical movement must still belong to the card controller when
      // its horizontal component happens to be larger; otherwise the browser
      // scrolls the document natively and can leave it parked between cards.
      if (event.ctrlKey || (window.visualViewport && visualViewport.scale !== 1) || excluded(event) || !event.cancelable) return;
      var delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? innerHeight : 1);
      if (!delta) return;
      var now = performance.now();
      var direction = Math.sign(delta), magnitude = Math.abs(delta);
      // Trackpads expose wheel packets, not touch-start/end. A new acceleration
      // after a decaying tail is a fresh swipe even without a long silent gap.
      var renewed = wheelTail && magnitude >= 12 && magnitude > wheelMagnitude * 2.5;
      var fresh = !gesture || now - lastWheel > 180 || direction !== wheelDirection || renewed;
      if (fresh) {
        var landing = tween && direction === wheelDirection ? tween.to : undefined;
        begin(landing);
        wheelPeak = magnitude; wheelTail = false;
      } else if (tween && !gesture.boundary) {
        cancelAnimationFrame(frame); frame = 0; tween = null;
        // Preserve the requested position. Resetting target to scrollY here
        // discarded unpainted input every time packets arrived 110–180ms apart.
      }
      wheelPeak = Math.max(wheelPeak, magnitude);
      if (magnitude < wheelMagnitude && magnitude <= Math.max(6, wheelPeak * .2)) wheelTail = true;
      wheelMagnitude = magnitude; wheelDirection = direction;
      lastWheel = now;
      var last = gesture.anchors.length - 1, edge = gesture.anchors[last];
      var continuous = gesture.interior || (gesture.index === last && direction > 0);
      if (continuous && !gesture.boundary && window.scrollY + delta >= edge) {
        // The OS already supplies trackpad momentum. Do not filter it a second
        // time or schedule a separate settle on ordinary product/footer scroll.
        stop(); gesture.native = true; target = window.scrollY;
        gesture.travel += delta; gesture.direction = direction;
        return;
      }
      event.preventDefault();
      if (continuous) {
        gesture.native = false; gesture.boundary = true;
        gesture.lower = edge;
        target = edge;
        if (!tween && Math.abs(window.scrollY - edge) > .5) animate(edge);
        return;
      }
      gesture.travel += delta;
      gesture.direction = Math.sign(delta);
      target = clamp(target + delta, gesture.lower, gesture.upper);
      follow();
      clearTimeout(idle); idle = setTimeout(finishWheel, 180);
    }
    function startTouch(event) {
      if (event.touches.length !== 1) { touch = null; stop(); return; }
      var nativeReturn = nativeScroll() && options.nativeReturnBoundary && event.target.closest &&
        !event.target.closest('.mwy-nav-reveal, .drawer, [role="dialog"], .dropdown-menu, a, button, summary, input, textarea, select, [contenteditable="true"], [data-native-scroll]');
      if (excluded(event) && !nativeReturn) { touch = null; stop(); return; }
      var point = event.touches[0];
      if (nativeScroll()) {
        // A standalone article normally keeps native scrolling. Retain just
        // enough gesture state to take an upward return back before its card
        // becomes visible; otherwise the counter-transform trails Safari by a
        // frame at that boundary.
        gesture = null; stop();
        touch = options.nativeReturnBoundary ? {
          x: point.clientX, y: point.clientY, lastY: point.clientY,
          time: performance.now(), moving: false, native: true
        } : null;
        return;
      }
      begin();
      touch = { x: point.clientX, y: point.clientY, lastY: point.clientY, time: performance.now(), moving: false };
    }
    function moveTouch(event) {
      if (!touch || event.touches.length !== 1) { touch = null; return; }
      var point = event.touches[0], now = performance.now();
      if (touch.native) {
        var nativeDelta = touch.lastY - point.clientY;
        var returnBoundary = options.nativeReturnBoundary();
        touch.lastY = point.clientY; touch.time = now;
        // Finger moving down means the page is returning upward. Take control
        // slightly before the visual boundary so the first revealed card frame
        // is already held in place.
        if (nativeDelta >= 0 || window.scrollY > returnBoundary + 48) return;
        if (!event.cancelable) { touch = null; return; }
        event.preventDefault();
        begin();
        touch.native = false;
        touch.moving = true;
        return;
      }
      if (!touch.moving) {
        var horizontal = Math.abs(point.clientX - touch.x), vertical = Math.abs(point.clientY - touch.y);
        if (Math.max(horizontal, vertical) < 6) return;
        // Only a deliberate sideways gesture leaves the vertical card flow.
        if (horizontal >= 24 && horizontal > vertical * 3 && vertical < 12) { touch = null; return; }
      }
      if (!event.cancelable) { touch = null; return; }
      event.preventDefault();
      var delta = touch.lastY - point.clientY;
      var elapsed = Math.max(8, now - touch.time);
      gesture.velocity = clamp(delta / elapsed, -3, 3);
      gesture.travel += delta;
      gesture.direction = Math.sign(delta);
      target = clamp(target + delta, gesture.lower, gesture.upper);
      write(target); // direct manipulation: content stays attached to the finger
      touch.lastY = point.clientY; touch.time = now; touch.moving = true;
    }
    function endTouch(event) {
      if (!touch) return;
      var moved = touch.moving, stale = performance.now() - touch.time > 90;
      touch = null;
      if (!moved) return;
      if (stale || event.type === 'touchcancel') gesture.velocity = 0;
      var to = destination();
      if (gesture.interior || (gesture.index === gesture.anchors.length - 1 && gesture.travel > 0)) {
        // Momentum remains inside the gesture's region. It can settle at that
        // region's edge but cannot consume the next card in the same swipe.
        var velocity = reduced.matches ? 0 : gesture.velocity;
        var previous = performance.now();
        function coast(now) {
          var dt = Math.max(0, now - previous); previous = now;
          var decay = Math.exp(-dt / 260);
          var next = clamp(window.scrollY + velocity * 260 * (1 - decay), gesture.lower, gesture.upper);
          velocity *= decay;
          write(next);
          if (Math.abs(velocity) > .025 && next > gesture.lower && next < gesture.upper) frame = requestAnimationFrame(coast);
          else frame = 0;
        }
        frame = requestAnimationFrame(coast);
      } else animate(to);
    }
    function key(event) {
      if (nativeScroll()) { stop(); gesture = null; return; }
      if (event.altKey || event.metaKey || event.ctrlKey || (event.target !== document.body && excluded(event)) || event.target.closest('a, button, summary')) return;
      var delta = { ArrowDown: 80, ArrowUp: -80, PageDown: innerHeight * .8, PageUp: -innerHeight * .8, ' ': innerHeight * (event.shiftKey ? -.8 : .8) }[event.key];
      if (!delta) return;
      event.preventDefault(); begin(); gesture.travel = delta;
      target = clamp(target + delta, gesture.lower, gesture.upper);
      animate(destination());
    }
    function listen(type, handler, passive) { document.addEventListener(type, handler, { passive: passive, signal: abort.signal }); }
    listen('wheel', wheel, false);
    // Tablet swipes stay on Safari's native scrolling path. Registering a
    // blocking touchmove handler forces every swipe through the main thread.
    var nativeTabletTouch = navigator.maxTouchPoints > 0 &&
      Math.min(screen.width, screen.height) >= 600;
    if (!nativeTabletTouch) {
      listen('touchstart', startTouch, true);
      listen('touchmove', moveTouch, false);
      listen('touchend', endTouch, true);
      listen('touchcancel', endTouch, true);
    }
    listen('keydown', key, false);
    return {
      busy: function () { return !!(frame || touch || idle); },
      go: function (y, instant) { stop(); touch = gesture = null; target = y; if (instant) write(y); else animate(y); },
      destroy: function () {
        stop(); abort.abort(); gesture = touch = null;
        if (originalSnap) root.style.setProperty('scroll-snap-type', originalSnap, originalSnapPriority); else root.style.removeProperty('scroll-snap-type');
        if (originalBehavior) root.style.setProperty('scroll-behavior', originalBehavior, originalBehaviorPriority); else root.style.removeProperty('scroll-behavior');
      }
    };
  };
}());
